import { Fragment, useState } from 'react';
import { Modal } from '@sandtech-hub-monorepo/ui-kit';
import {
  AcademicStandingBanner,
  AppealStatusBanner,
  formatGPA,
} from '@sandtech-hub-monorepo/ui-kit-site';
import { StandingDetailModal } from './modals/StandingDetailModal';
import { AppealFormModal } from './modals/AppealFormModal';
import { AppealDetailModal } from './modals/AppealDetailModal';
import {
  canAppealCase,
  ConductCase,
  getActiveCases,
  getAdminMessage,
  getAppealResolvedAt,
  getCaseDescription,
  getSeverityUpdate,
  SEVERITY_TO_STANDING,
} from './conductCaseUtils';

type ModalState = {
  type: 'standing' | 'appealForm' | 'appealDetail';
  caseId: string;
} | null;

interface Props {
  conductCases: ConductCase[];
  cumulativeGPA: number | null | undefined;
}

export const ActiveConductCasesBanners = ({
  conductCases,
  cumulativeGPA,
}: Props) => {
  const [modalState, setModalState] = useState<ModalState>(null);

  const activeCases = getActiveCases(conductCases);

  if (activeCases.length === 0) {
    return null;
  }

  const selectedCase = modalState
    ? activeCases.find((c) => c.id === modalState.caseId) ?? null
    : null;

  const closeModal = () => setModalState(null);

  return (
    <>
      <div className="flex flex-col gap-2 mt-2">
        {activeCases.map((c) => (
          <Fragment key={c.id}>
            <AcademicStandingBanner
              academicStanding={SEVERITY_TO_STANDING[c.severity]}
              reason={getCaseDescription(c)}
              onReadMore={() =>
                setModalState({ type: 'standing', caseId: c.id })
              }
            />
            {c.appealStatus && (
              <AppealStatusBanner
                appealStatus={c.appealStatus}
                onViewDetails={() =>
                  setModalState({ type: 'appealDetail', caseId: c.id })
                }
              />
            )}
          </Fragment>
        ))}
      </div>

      {selectedCase && (
        <>
          <Modal
            open={modalState?.type === 'standing'}
            onOpenChange={(open) => !open && closeModal()}
          >
            <StandingDetailModal
              closeModal={closeModal}
              severity={selectedCase.severity}
              category={selectedCase.category}
              reason={getCaseDescription(selectedCase)}
              canAppeal={canAppealCase(selectedCase)}
              onAppeal={() =>
                setModalState({ type: 'appealForm', caseId: selectedCase.id })
              }
            />
          </Modal>
          <Modal
            open={modalState?.type === 'appealForm'}
            onOpenChange={(open) => !open && closeModal()}
          >
            <AppealFormModal
              closeModal={closeModal}
              caseId={selectedCase.id}
              severity={selectedCase.severity}
              category={selectedCase.category}
            />
          </Modal>
          {selectedCase.appealStatus && (
            <Modal
              open={modalState?.type === 'appealDetail'}
              onOpenChange={(open) => !open && closeModal()}
            >
              <AppealDetailModal
                appealStatus={selectedCase.appealStatus}
                appealSubmittedAt={selectedCase.appealSubmittedAt ?? ''}
                messageFromAdmin={getAdminMessage(selectedCase)}
                appealResolvedAt={getAppealResolvedAt(selectedCase)}
                probationStatus={
                  cumulativeGPA != null
                    ? `${formatGPA(cumulativeGPA)} GPA`
                    : null
                }
                category={selectedCase.category}
                previousSeverity={
                  getSeverityUpdate(selectedCase)?.previousSeverity
                }
                newSeverity={getSeverityUpdate(selectedCase)?.newSeverity}
              />
            </Modal>
          )}
        </>
      )}
    </>
  );
};
